import React from "react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { COMPANY } from "@/data/content";

const Section = ({ title, children }) => (
  <div className="mb-12">
    <h2 className="font-serif text-2xl sm:text-3xl text-[#F5F5F0] mb-4">{title}</h2>
    <div className="space-y-4 text-[#A1A1AA] font-light leading-relaxed">{children}</div>
  </div>
);

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="bg-[#0A0A0B]">
      <section className="pt-40 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Reveal>
            <SectionLabel className="mb-6">Legal</SectionLabel>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#F5F5F0]">Privacy Policy</h1>
            <p className="mt-6 text-[#A1A1AA] font-light">
              Last updated: {new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="text-[#A1A1AA] font-light leading-relaxed mb-12">
              Blackthorne Stratton ("we", "us", "our") is committed to protecting and respecting your
              privacy. This Privacy Policy explains how we collect, use, store and protect your personal
              data when you interact with us or use this website. We process personal data in accordance
              with the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018, the
              Privacy and Electronic Communications Regulations (PECR), and align our information security
              practices with the principles of the ISO/IEC 27001 information security management standard.
            </p>

            <Section title="1. Data Controller">
              <p>
                Blackthorne Stratton is the data controller responsible for your personal data. For any
                questions about this policy or how we handle your data, contact us at{" "}
                <a href={`mailto:${COMPANY.email}`} className="text-[#C9A24B] hover:underline">{COMPANY.email}</a>.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <p>We may collect and process the following categories of personal data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Identity and contact details (name, email address, telephone number);</li>
                <li>Information you provide via our enquiry or recruitment forms, including your message, service of interest or position applied for;</li>
                <li>Professional background and qualifications where submitted for recruitment purposes;</li>
                <li>Technical data such as IP address, browser type and usage data collected via cookies and similar technologies.</li>
              </ul>
            </Section>

            <Section title="3. How We Use Your Data & Lawful Basis">
              <p>We process your personal data on the following lawful bases under UK GDPR:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><span className="text-[#F5F5F0]">Consent</span> — where you submit an enquiry or recruitment application;</li>
                <li><span className="text-[#F5F5F0]">Legitimate interests</span> — to respond to enquiries, provide our services, and operate and improve our business, provided your rights do not override these interests;</li>
                <li><span className="text-[#F5F5F0]">Contractual necessity</span> — to take steps at your request prior to entering into a contract;</li>
                <li><span className="text-[#F5F5F0]">Legal obligation</span> — to comply with applicable laws and regulatory requirements.</li>
              </ul>
            </Section>

            <Section title="4. Data Sharing & Disclosure">
              <p>
                Given the sensitive nature of our work, we maintain strict confidentiality. We do not sell
                your personal data. We may share data only with trusted service providers acting on our
                behalf under appropriate contractual safeguards, or where required by law, regulation, or
                a competent authority.
              </p>
            </Section>

            <Section title="5. International Transfers">
              <p>
                As we operate in London and worldwide, your data may be transferred outside the UK. Where
                this occurs, we ensure appropriate safeguards are in place, such as adequacy regulations or
                International Data Transfer Agreements, to protect your information.
              </p>
            </Section>

            <Section title="6. Data Security">
              <p>
                We implement appropriate technical and organisational measures aligned with ISO/IEC 27001
                principles to protect personal data against unauthorised access, loss, alteration or
                disclosure. Access to personal data is restricted on a need-to-know basis, and we maintain
                ongoing review of our security controls.
              </p>
            </Section>

            <Section title="7. Data Retention">
              <p>
                We retain personal data only for as long as necessary to fulfil the purposes for which it
                was collected, including legal, accounting or reporting requirements. Enquiry and
                recruitment data is retained for a reasonable period and securely deleted when no longer
                required.
              </p>
            </Section>

            <Section title="8. Your Rights">
              <p>Under UK GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access the personal data we hold about you;</li>
                <li>Request rectification of inaccurate data;</li>
                <li>Request erasure of your data ("right to be forgotten");</li>
                <li>Restrict or object to processing;</li>
                <li>Data portability;</li>
                <li>Withdraw consent at any time.</li>
              </ul>
              <p>
                To exercise any of these rights, contact us at{" "}
                <a href={`mailto:${COMPANY.email}`} className="text-[#C9A24B] hover:underline">{COMPANY.email}</a>.
                You also have the right to lodge a complaint with the Information Commissioner's Office
                (ICO), the UK supervisory authority, at ico.org.uk.
              </p>
            </Section>

            <Section title="9. Cookies">
              <p>
                Our website may use cookies and similar technologies to ensure functionality and analyse
                usage. You can manage cookie preferences through your browser settings. Essential cookies
                are necessary for the website to operate.
              </p>
            </Section>

            <Section title="10. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this
                page with a revised "last updated" date.
              </p>
            </Section>

            <Section title="11. Contact">
              <p>
                For any privacy-related queries or to exercise your rights, please contact:{" "}
                <a href={`mailto:${COMPANY.email}`} className="text-[#C9A24B] hover:underline">{COMPANY.email}</a>{" "}
                · {COMPANY.phone} · {COMPANY.location}.
              </p>
            </Section>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
